import {ChangeDetectionStrategy, Component, DestroyRef, OnInit, ViewEncapsulation} from '@angular/core';
import {VisitContext} from "../../visit-context";
import {AppContext} from "../../../app-context";
import {
  DeclarationStatus,
  DownloadableFile,
  HarbourDuesDeclaration,
  HarbourDuesDiscount,
  HarbourDuesStevedore,
  HarbourDuesVisit,
  LinerService,
  PurposeCode,
  StoreHarbourDuesDeclaration,
  TaskMessageStatus
} from "@portbase/bezoekschip-service-typescriptmodels";
import {FormBuilder, FormGroup} from "@angular/forms";
import {PortvisitUtils} from "../../../refdata/portvisit-utils";
import moment from "moment-timezone";
import {openConfirmationModalWithCallback, sendCommand, sendQuery} from "../../../common/utils";
import {Observable, of} from "rxjs";
import {
  CopyDataConfirmedEvent,
  DiscountSelection,
  HarbourDuesBerthVisitForm,
  HarbourDuesDeclarationTab
} from "../types";
import {HarbourDuesService} from "../services/harbour-dues.service";
import {TabInfo} from "../../../common/tab-panel/tab-panel.component";
import {ModalConfirmAutofocus, ModalConfirmAutofocusData} from "../../../common/modal/modal-confirm.component";

import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {NewPortvisitUtils} from "../../../new-portvisit/new-portvisit.utils";
import {HarbourDuesCargoService} from "../services/harbour-dues-cargo.service";
import {sendCommandAndForget} from "../../../common/handlers/app-common-utils";

@Component({
  selector: 'app-harbour-dues-amsterdam',
  templateUrl: './harbour-dues-amsterdam.component.html',
  styleUrls: ['./harbour-dues-amsterdam.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class HarbourDuesAmsterdamComponent implements OnInit {

  declarations: HarbourDuesDeclaration[] = [];
  selectedDeclaration: HarbourDuesDeclaration;
  form = this.initForm(new FormBuilder());
  showValidationErrors: boolean;
  stevedores: Observable<HarbourDuesStevedore[]> = sendQuery('com.portbase.bezoekschip.common.api.harbourdues.queries.GetHarbourDuesStevedores', {})
  declarationTabs: HarbourDuesDeclarationTab[] = [];
  protected readonly PortvisitUtils = PortvisitUtils;
  protected readonly context = VisitContext;
  protected readonly appContext = AppContext;
  protected readonly sent = TaskMessageStatus.ACCEPTED;
  protected port: string = "";


  constructor(
    private service: HarbourDuesService,
    private destroyRef: DestroyRef,
    private cargoService: HarbourDuesCargoService) {}

  private initForm(fb: FormBuilder) {
    return fb.group({
      discount: fb.group({
        discountType: fb.control<string>(null),
        greenAward: fb.control(null),
        linerService: fb.control<LinerService>(null),
        shortSea: fb.control(null),
      }),
      exemption: fb.control<string>(null),
      selectedType: fb.control<DiscountSelection>(DiscountSelection.NO_DISCOUNT),
      berthVisits: fb.array<FormGroup<HarbourDuesBerthVisitForm>>([])
    });
  }

  ngOnInit(): void {
    if (!this.isAvailable()) {
      return;
    }
    this.service.getDeclarations(this.context.savedVisit.crn).subscribe(declarations => {
      this.declarations = declarations;
      const portVisit = this.context.savedVisit.visitDeclaration.portVisit;
      const ataPort = portVisit.ataPort;
      const atdPort = portVisit.atdPort;
      this.declarationTabs = this.service.declarationTabs(this.context.savedVisit.crn, ataPort, atdPort);
      this.selectDeclaration(this.declarationTabs[this.declarationTabs.length - 1]);
    });

    this.service.statusUpdates().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(update => {
      this.declarations.concat(this.selectedDeclaration).filter(d => d.crn === update.crn && d.statementNumber === update.statementNumber)
        .forEach(d => {
          d.rejectionReason = update.rejectReason;
          d.status = update.status;
        });
    });

    this.port = this.getPort();
  }

  selectDeclaration(tab: HarbourDuesDeclarationTab) {
    this.selectedDeclaration = this.service.select(tab, this.declarations);
    if (this.canEdit()) {
      this.selectedDeclaration = this.service.syncWithVisit(this.selectedDeclaration, this.context.savedVisit);
      this.service.syncWithPreviousDeclaration(this.selectedDeclaration, this.declarations);
    }
    this.patchForm(this.selectedDeclaration)
  }

  private patchForm(declaration: HarbourDuesDeclaration) {
    this.form.controls.exemption.patchValue(declaration.exemption);
    this.form.controls.discount.patchValue(declaration.discount);
    this.form.controls.selectedType.patchValue(this.getSelectedDiscount(declaration.discount, declaration.exemption));
    this.form.controls.berthVisits.clear();
    let fb = new FormBuilder();
    declaration.visit.berthVisits.map(value =>
      fb.group<HarbourDuesBerthVisitForm>({
        berthVisitId: fb.control(value.berthVisitId),
        stevedore: fb.control(value.stevedore),
        dischargedCargoCounts: fb.array(value.dischargedCargoCounts.map(v => this.service.newCargoCount(v))),
        loadedCargoCounts: fb.array(value.loadedCargoCounts.map(v => this.service.newCargoCount(v))),
        dischargedGoods: fb.array(value.dischargedGoods.map(v => this.service.newGoodsItem(v))),
        loadedGoods: fb.array(value.loadedGoods.map(v => this.service.newGoodsItem(v)))
      })).forEach(value => {
      this.form.controls.berthVisits.push(value);
    });
  }

  saveDraft() {
    this.showValidationErrors = false;
    this.doSave(false);
  }

  declare() {
    this.showValidationErrors = true;
    if (this.form.invalid) {
      AppContext.registerError('Please review the fields with errors.');
    } else {
      AppContext.clearVisitAlerts();

      if (this.port == "Amsterdam") {
        openConfirmationModalWithCallback((confirmed) => {
          if (confirmed === null) {
            return;
          }
          if (confirmed) {
            this.doSave(true);
          }
        }, ModalConfirmAutofocus, <ModalConfirmAutofocusData>{
          type: "primary",
          title: "Ready to send?",
          message: "Once sent, you can't make any changes.",
          messageSeperated: true,
          confirmText: "Send",
          cancelText: "Cancel",
          noBorder: true,
          alignHeaderBody: true
        }, 'static');
      } else if (this.port == "Rotterdam") {
        this.doSave(true)
      }
    }
  }

  doSave(declare: boolean) {
    let formData = this.form.value;

    let mergedBerthVisits = this.selectedDeclaration.visit.berthVisits.map((element, index) => {
      let formBerthVisitValue = formData.berthVisits[index];
      return {...element, ...formBerthVisitValue};
    });

    let command: StoreHarbourDuesDeclaration = {
      crn: this.selectedDeclaration.crn,
      declare: declare,
      discount: formData.discount,
      exemption: formData.exemption,
      statementNumber: this.selectedDeclaration.statementNumber,
      visit: {...this.selectedDeclaration.visit, berthVisits: mergedBerthVisits}
    };
    sendCommand("com.portbase.bezoekschip.common.api.harbourdues.commands.StoreHarbourDuesDeclaration", command, () => {
      AppContext.registerSuccess(`Harbour dues declaration ${declare ? 'declared' : 'saved'} successfully`);
      this.patchDeclaration(command.statementNumber, command.declare, command.discount, command.exemption, command.visit);
    });
  }

  patchDeclaration(statementNumber: number, declare: boolean, discount: HarbourDuesDiscount, exemption: string, visit: HarbourDuesVisit) {
    this.selectedDeclaration.status = declare ? DeclarationStatus.DECLARED : null;
    this.selectedDeclaration.discount = discount;
    this.selectedDeclaration.exemption = exemption;
    this.selectedDeclaration.visit = visit;
    let index = this.declarations.findIndex(value => value.statementNumber == statementNumber);
    if (index >= 0) {
      this.declarations[index] = this.selectedDeclaration;
    } else {
      this.declarations.push(this.selectedDeclaration);
    }
  }

  isAvailable(): boolean {
    return this.hasArrived() && this.isFinancialDeclarant();
  }

  hasArrived(): boolean {
    return this.context.savedVisit.visitDeclaration.portVisit.ataPort != null;
  }

  isFinancialDeclarant() {
    return this.appContext.isAdmin() || this.context.isFinancialDeclarant();
  }

  canEdit(): boolean {
    return this.isAvailable() &&
      this.declarationSelected() &&
      ![DeclarationStatus.ACCEPTED, DeclarationStatus.DECLARED].includes(this.declarationStatus());
  }

  declarationSelected() : boolean {
    return this.selectedDeclaration != null;
  }

  berthVisitDuration(ata: string, atd: string) {
    if (ata) {
      let start = moment(ata);
      let end = atd ? moment(atd) : moment();
      let duration = moment.duration(start.diff(end));
      return duration.humanize()
    }
    return "Unknown";
  }

  berthVisitArrivalDateTime(ata: string) {
    return moment(ata).format('D MMM YYYY, HH:mm');
  }

  visitDepartureOrArrivalDateTime(dateTime: string) {
    return dateTime ? moment(dateTime).format('D MMM yyyy [at] HH:mm') : 'Unknown';
  }

  getLoadedGoodsItems(index: number) {
    return this.form.controls.berthVisits.at(index).controls.loadedGoods.controls;
  }

  getDischargedGoodsItems(index: number) {
    return this.form.controls.berthVisits.at(index).controls.dischargedGoods.controls;
  }

  goodsLoadedAt(index: number) {
    return this.selectedDeclaration.visit.berthVisits[index].visitPurposes
      .find(value => value == PurposeCode.LOADING);
  }

  goodsDischargedAt(index: number) {
    return this.selectedDeclaration.visit.berthVisits[index].visitPurposes
      .find(value => value == PurposeCode.DISCHARGE || value == PurposeCode.DISCHARGE_CRUDE_OIL)
  }

  cargoTypesLoadedAt(index: number, cargoTypes: string[]) {
    let cargoTypesLoaded = this.getLoadedGoodsItems(index)
      .find(value =>
        cargoTypes.includes(value.controls.cargoType.value));
    return this.goodsLoadedAt(index) && cargoTypesLoaded;
  }

  cargoTypesDischargedAt(index: number, cargoTypes: string[]) {
    let cargoTypesDischarged = this.getDischargedGoodsItems(index)
      .find(value =>
        cargoTypes.includes(value.controls.cargoType.value));
    return this.goodsDischargedAt(index) && cargoTypesDischarged;
  }

  flatsOrContainersLoadedAt(index: number) {
    return this.cargoTypesLoadedAt(index, [
      "FLATS", "FLATS_ROLL_ON_ROLL_OFF", "FLATS_LIFT_ON_LIFT_OFF",
      "CONTAINERS_LIFT_ON_LIFT_OFF", "CONTAINERS_ROLL_ON_ROLL_OFF"
    ]);
  }

  flatsOrContainersDischargedAt(index: number) {
    return this.cargoTypesDischargedAt(index, [
      "FLATS", "FLATS_ROLL_ON_ROLL_OFF", "FLATS_LIFT_ON_LIFT_OFF",
      "CONTAINERS_LIFT_ON_LIFT_OFF", "CONTAINERS_ROLL_ON_ROLL_OFF"
    ]);
  }

  formatHarbourDuesStevedore(s: HarbourDuesStevedore){
    return s.code + " - " + s.name;
  }

  declarationHasBerthVisits() {
    return this.declarationSelected() && this.selectedDeclaration.visit.berthVisits.length > 0;
  }

  invoicePeriod(start: string, end: string) {
    let invoiceStart = moment(start).format('D MMM');
    let invoiceEnd = end ? moment(end).format('D MMM') : 'now';
    if (invoiceStart === invoiceEnd) {
      return invoiceStart;
    }
    return `${invoiceStart} - ${invoiceEnd}`.toLowerCase();
  }

  onTabChange($event: TabInfo) {
    this.selectDeclaration(this.declarationTabs[$event.index]);
  }

  getSelectedDiscount(a: HarbourDuesDiscount, b: string) {
    if (a && (a.discountType || a.greenAward)) {
      return DiscountSelection.DISCOUNT;
    } else if (b) {
      return DiscountSelection.EXEMPTION;
    } else {
      return DiscountSelection.NO_DISCOUNT;
    }
  }


  declarationSent() {
    return this.declarationSelected() &&
      [DeclarationStatus.DECLARED, DeclarationStatus.ACCEPTED, DeclarationStatus.REJECTED].includes(this.declarationStatus());
  }

  downloadPdf() {
    sendQuery('com.portbase.bezoekschip.common.api.harbourdues.queries.GetHarbourDuesDeclarationPdf',{
      crn: this.selectedDeclaration.crn, statementNumber: this.selectedDeclaration.statementNumber
    }, {caching: false, showSpinner: true}).subscribe((file: DownloadableFile) => {
      const a = document.createElement('a');
      a.href = `data:${(file.mediaType)};base64,${(file.data)}`;
      a.setAttribute('download', file.fileName);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      return new Promise(resolve => setTimeout(() => resolve(file), 100));
    });
  }

  getDeclarationPeriod() {
    if (this.declarationSelected() && this.declarationTabs.length > 1) {
      return this.invoicePeriod(this.selectedDeclaration.invoiceStart, this.selectedDeclaration.invoiceEnd);
    } else {
      return '';
    }
  }

  getPort() {
    if (this.context.savedVisit.portOfCall.port.locationUnCode == "NLAMS") {
      return "Amsterdam";
    } else if (this.context.savedVisit.portOfCall.port.locationUnCode == "NLRTM") {
      return "Rotterdam";
    }
  }

  getTariffLink() {
    if (this.port == "Amsterdam") {
      return "https://www.portofamsterdam.com/en/shipping/sea-shipping/harbour-dues";
    } else if (this.port == "Rotterdam") {

    } else {
      return ""
    }
  }

  declarationTaskMessageStatus() {
    if (this.declarationSelected() && this.selectedDeclaration.status) {
      return NewPortvisitUtils.declarationStatusToTaskMessageStatus(this.selectedDeclaration.status);
    }
    return TaskMessageStatus.UNKNOWN;
  }

  declarationAccepted() {
    return [DeclarationStatus.ACCEPTED].includes(this.declarationStatus())
  }

  declarationRejectionReason() {
    return this.selectedDeclaration?.rejectionReason;
  }

  declarationStatus() {
    return this.selectedDeclaration?.status;
  }

  handle(event: CopyDataConfirmedEvent) {
    sendCommandAndForget("com.portbase.bezoekschip.common.api.harbourdues.events.CopyDataConfirmed", {
      crn: this.context.savedVisit.crn,
      terminalCode: event.terminalCode,
      dataType: event.type
    });
    let controls = this.form.controls.berthVisits.at(event.index).controls;
    let source = event.type === 'discharge' ? this.cargoService.getDischargedGoodsByTerminalCode(this.context.savedVisit.crn) : of(this.cargoService.getLoadedGoodsByTerminalCode(this.context.savedVisit));
    let target = event.type === 'discharge' ? controls.dischargedGoods : controls.loadedGoods;
    source.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(result => {
      if (result.has(event.terminalCode) && result.get(event.terminalCode).length > 0) {
        target.clear();
        for (const harbourDuesGoodsItem of result.get(event.terminalCode)) {
          target.push(this.service.newGoodsItem(harbourDuesGoodsItem));
        }
      }

    })
  }

  combinedLoadingWeightExceedsVesselDeadWeight() {
    const {weightLoadedGoods, weightDischargedGoods, deadWeight} = this.summaryWeights();
    return deadWeight && ((weightLoadedGoods - weightDischargedGoods) > deadWeight)
  }

  combinedDischargeWeightExceedsVesselDeadWeight() {
    const {weightLoadedGoods, weightDischargedGoods, deadWeight} = this.summaryWeights();
    return deadWeight && ((weightDischargedGoods - weightLoadedGoods) > deadWeight)
  }

  summaryWeights() {
    let weightLoadedGoods = 0;
    let weightDischargedGoods = 0;
    let deadWeight = this.context.getVesselDeadWeightKg();

    for (let i = 0; i < this.form.controls.berthVisits.length; i++) {
      weightLoadedGoods += this.form.controls.berthVisits.at(i).controls.loadedGoods.controls
        .map(value => value.controls.weight.value || 0)
        .reduce((a, b) => a + b, 0);
      weightDischargedGoods += this.form.controls.berthVisits.at(i).controls.dischargedGoods.controls
        .map(value => value.controls.weight.value || 0)
        .reduce((a, b) => a + b, 0);
    }

    return {weightLoadedGoods: weightLoadedGoods, weightDischargedGoods: weightDischargedGoods, deadWeight: deadWeight}
  }
}
